import { useEffect, useState } from "react";
import { Navigate, Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { toast } from "sonner";
import { Check, ArrowRight, Sparkles, Mail, ExternalLink } from "lucide-react";

const ALL_CATS = [
  { slug: "travel", name: "Travel" },
  { slug: "clothing", name: "Clothing" },
  { slug: "auto", name: "Auto" },
  { slug: "stocks", name: "Stocks" },
  { slug: "crypto", name: "Crypto" },
  { slug: "electronics", name: "Electronics" },
  { slug: "home", name: "Home" },
  { slug: "food", name: "Food & Dining" },
  { slug: "beauty", name: "Beauty" },
  { slug: "gaming", name: "Gaming" },
  { slug: "health", name: "Health & Wellness" },
];

const TARGET = 3;

export default function Preferences() {
  const { user, loading: authLoading, refresh } = useAuth();
  const [params] = useSearchParams();
  const isWelcome = params.get("welcome") === "1";
  const navigate = useNavigate();
  const [selected, setSelected] = useState([]);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => {
    if (user?.preferences?.categories) setSelected(user.preferences.categories);
  }, [user]);

  const loadPreview = async () => {
    if (isWelcome) return; // hide preview during onboarding
    setPreviewLoading(true);
    try {
      const { data } = await api.get("/newsletter/preview");
      setPreview(data);
    } catch (e) { setPreview(null); }
    finally { setPreviewLoading(false); }
  };

  useEffect(() => { if (user && !isWelcome) loadPreview(); }, [user, isWelcome]);

  if (authLoading) return <div className="max-w-3xl mx-auto px-6 py-12">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;

  const toggle = (slug) => {
    setSelected((prev) => (prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug]));
  };

  const save = async (afterSave = "stay") => {
    setSaving(true);
    try {
      await api.put("/auth/me/preferences", { categories: selected });
      toast.success(isWelcome ? "You're all set — welcome aboard." : "Preferences saved.");
      await refresh();
      if (afterSave === "home") navigate("/");
      else loadPreview();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const skip = () => navigate("/");
  const reached = selected.length >= TARGET;
  const previewDeal = preview?.deal;

  return (
    <div className="max-w-3xl mx-auto px-6 py-12" data-testid={isWelcome ? "onboarding-page" : "preferences-page"}>
      <div className="mb-10">
        {isWelcome && (
          <div className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.25em] text-white bg-[#E63946] px-2 py-1 mb-4">
            <Sparkles className="w-3.5 h-3.5" strokeWidth={2} /> Welcome to DealHub · Step 1 of 1
          </div>
        )}
        {!isWelcome && (
          <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#E63946] mb-3">Your daily digest</div>
        )}
        <h1 className="font-serif font-black text-5xl md:text-6xl tracking-tighter">
          {isWelcome ? <>Pick your <span className="italic font-normal">3 favorites.</span></> : <>Only what you<br /><span className="italic font-normal">actually want.</span></>}
        </h1>
        <p className="text-black/60 mt-4 max-w-xl">
          {isWelcome
            ? "Tomorrow morning's 8am email will be one hand-picked deal from a category you actually care about. Choose three to teach us your taste."
            : "Pick the categories you care about and our 8am digest will send you the best deal from one of them — instead of the site-wide featured pick."}
        </p>
      </div>

      <div className="border-2 border-black bg-white p-6 md:p-8 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-black/50">Pick your categories</div>
          <div className="text-xs font-mono uppercase tracking-widest" data-testid="pref-count">
            {isWelcome ? (
              <span className={reached ? "text-emerald-600" : "text-black/50"}>
                {selected.length} / {TARGET} {reached && "✓"}
              </span>
            ) : (
              <span className="text-black/50">{selected.length === 0 ? "Site-wide pick" : `${selected.length} selected`}</span>
            )}
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="pref-grid">
          {ALL_CATS.map((c) => {
            const on = selected.includes(c.slug);
            return (
              <button
                key={c.slug}
                onClick={() => toggle(c.slug)}
                className={`flex items-center justify-between border px-4 py-3 text-sm text-left ${on ? "bg-black text-white border-black" : "border-black/20 hover:border-black"}`}
                data-testid={`pref-${c.slug}`}
              >
                <span className="font-serif font-bold">{c.name}</span>
                {on && <Check className="w-4 h-4 shrink-0" strokeWidth={2} />}
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex items-center justify-between gap-4">
        {isWelcome ? (
          <>
            <button
              onClick={skip}
              className="text-xs font-bold uppercase tracking-widest text-black/50 hover:text-black underline underline-offset-4"
              data-testid="onboarding-skip"
            >
              Skip for now
            </button>
            <button
              onClick={() => save("home")}
              disabled={saving || selected.length === 0}
              className="inline-flex items-center gap-2 bg-black text-white px-6 py-3 font-bold uppercase tracking-widest text-xs hover:bg-[#E63946] disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="onboarding-continue"
            >
              {saving ? "Saving…" : (selected.length < TARGET ? `Pick ${TARGET - selected.length} more` : "Start exploring")}
              <ArrowRight className="w-4 h-4" strokeWidth={2} />
            </button>
          </>
        ) : (
          <>
            <button
              onClick={() => save("stay")}
              disabled={saving}
              className="bg-black text-white px-6 py-3 font-bold uppercase tracking-widest text-xs hover:bg-[#E63946] disabled:opacity-50"
              data-testid="pref-save"
            >
              {saving ? "Saving…" : "Save preferences"}
            </button>
            <Link to="/bookmarks" className="text-xs font-bold uppercase tracking-widest hover:text-[#E63946]">
              Back to bookmarks
            </Link>
          </>
        )}
      </div>

      {/* Digest Preview — only outside onboarding */}
      {!isWelcome && (
        <div className="mt-12 border border-black/10 bg-white" data-testid="digest-preview">
          <div className="p-5 border-b border-black/10 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Mail className="w-4 h-4 text-[#E63946]" strokeWidth={2} />
              <div className="font-serif font-bold text-lg">Your next digest</div>
              {preview?.source && (
                <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 ${preview.source === "personalized" ? "bg-emerald-100 text-emerald-700" : "bg-black/10 text-black/60"}`} data-testid="digest-source">
                  {preview.source === "personalized" ? "Personalized" : "Site-wide fallback"}
                </span>
              )}
            </div>
            <span className="text-[10px] font-mono uppercase tracking-widest text-black/50">Preview</span>
          </div>

          {previewLoading ? (
            <div className="p-6 text-xs font-mono uppercase tracking-widest text-black/40">Rendering…</div>
          ) : !previewDeal ? (
            <div className="p-6 text-sm text-black/60">No deal available yet — add some categories and check back.</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-5">
              <div className="sm:col-span-2 bg-black/5 aspect-[4/3] sm:aspect-auto relative">
                {previewDeal.image_url ? (
                  <img src={previewDeal.image_url} alt="" className="absolute inset-0 w-full h-full object-cover" />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center text-black/15 font-serif text-7xl">◇</div>
                )}
                {previewDeal.discount && (
                  <div className="absolute top-3 left-3 bg-[#E63946] text-white text-[11px] font-bold uppercase tracking-widest px-2.5 py-1">
                    {previewDeal.discount}
                  </div>
                )}
              </div>
              <div className="sm:col-span-3 p-5">
                <div className="text-[10px] font-mono uppercase tracking-widest text-black/50 mb-2">
                  {previewDeal.category} · {previewDeal.merchant}
                </div>
                <div className="font-serif font-black text-xl leading-tight mb-2" data-testid="digest-title">
                  {previewDeal.title}
                </div>
                <p className="text-sm text-black/60 mb-4 leading-relaxed line-clamp-3">
                  {previewDeal.description}
                </p>
                <a
                  href={previewDeal.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-[#E63946] hover:text-black"
                  data-testid="digest-open"
                >
                  Shop the deal <ExternalLink className="w-3.5 h-3.5" strokeWidth={2} />
                </a>
              </div>
            </div>
          )}
          <div className="p-4 border-t border-black/10 text-[10px] font-mono uppercase tracking-widest text-black/40">
            {preview?.categories?.length
              ? `Picked from your categories: ${preview.categories.join(", ")}`
              : "Set categories above to personalize your digest"}
          </div>
        </div>
      )}

      {isWelcome && (
        <p className="text-xs text-black/40 font-mono uppercase tracking-widest mt-8 text-center">
          You can change these anytime from the settings icon in the header
        </p>
      )}
    </div>
  );
}
